// features/projects/templates/professional/index.js
// ═══════════════════════════════════════════════════════════
// PROFESSIONAL TEMPLATE — top-level shell with 4 tabs.
//
// Renders when project.workspace_metadata.template === 'professional'.
// Wired in via features/projects/index.js's template dispatch.
//
// The 4 tabs (per docs/PROFESSIONAL_TEMPLATE_SPEC.md §II):
//   Dashboard  — 5-section synthesis briefing (Session 2: widget grid)
//   Chat       — reuses ProjectWorkstation embedded
//   Resources  — public sources toggle + per-project files (Session 5)
//   Simulator  — Cases · Hypotheses · Sources sub-tabs lifted from
//                features/lab-feature.js (Session 4)
//
// This commit is Session 1 of the Professional build sequence:
// shell + dispatch + stubs only. Subsequent sessions fill each tab.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import Head from 'next/head';
import { L, FONT, GRAPH_BACKDROP } from './tokens';
import ProjectWorkstation from '../../project-workstation';
import ProfessionalDashboard from './dashboard';
import ProfessionalResources from './resources';
import ProfessionalSimulator from './simulator';

const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Mono:wght@300;400;500&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap';

const NOOP = () => {};

const TABS = [
  { key: 'dashboard',  label: 'Dashboard',  color: L.gold,
    description: 'Synthesis briefing across your project' },
  { key: 'chat',       label: 'Chat',       color: L.sage,
    description: 'Think and iterate' },
  { key: 'resources',  label: 'Resources',  color: L.steel,
    description: 'Sources and files for this project' },
  { key: 'simulator',  label: 'Simulator',  color: L.gold,
    description: 'Cases, hypotheses, and methodology workspace' },
];

export default function ProfessionalWorkstation({
  userId,
  userRecord,
  supabase,
  workspaceSession,
  voiceConfig,
  onExit,
  onSidebarCollapse,
}) {
  const workspaceId = workspaceSession?.id;
  // Lab API endpoints (lifted into Simulator + Resources tabs) query
  // by lab_projects.id. The migration aligns lab_projects.id with
  // projects.id, so we pull projects.id from workspace_metadata and
  // pass it as a separate prop. workspaceId stays the shared_spaces
  // id used for chat / dashboard / etc.
  const projectId = workspaceSession?.workspace_metadata?.project_id || null;
  const projectTitle = workspaceSession?.name || 'Professional Project';

  // Tab persistence — keyed by workspace
  const storageKey = `professional_tab_${workspaceId}`;
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'dashboard';
    return window.localStorage.getItem(storageKey) || 'dashboard';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, activeTab);
  }, [storageKey, activeTab]);

  // Collapse parent sidebar while the workstation is open
  useEffect(() => {
    onSidebarCollapse?.(true);
    return () => onSidebarCollapse?.(false);
  }, [onSidebarCollapse]);

  return (
    <div
      style={{
        position: 'absolute', inset: 0,
        // Observatory base — graph-paper grid on midnight night.
        // Subtle enough to disappear under content; visible enough
        // to give the workspace a sense of measured depth.
        background: GRAPH_BACKDROP,
        backgroundSize: '16px 16px',
        color: L.text,
        fontFamily: FONT.sans,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Head>
        <link href={FONT_LINK} rel="stylesheet" />
      </Head>

      {/* Top bar — Observatory navigation strip */}
      <div
        style={{
          display: 'flex', alignItems: 'center',
          padding: '0 20px', height: 48,
          borderBottom: `1px solid ${L.b1}`,
          background: L.s1, flexShrink: 0, gap: 14,
          // Brass underline-glow at the bottom edge — the kind of
          // warm hairline you'd see under a brass nameplate.
          boxShadow: `inset 0 -1px 0 0 ${L.brass}30`,
        }}
      >
        <div style={{
          fontFamily: FONT.serif, fontSize: 18, fontWeight: 400,
          display: 'flex', alignItems: 'center', gap: 10,
          letterSpacing: '0.01em',
        }}>
          <span style={{ color: L.brass, fontSize: 16 }}>⌬</span>
          <span style={{ color: L.moonglow }}>{projectTitle}</span>
        </div>
        <div style={{ width: 1, height: 16, background: L.b2 }} />
        <div style={{
          fontSize: 9, color: L.brass, fontFamily: FONT.mono,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          fontWeight: 500,
        }}>
          Observatory
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 24 }}>
          {TABS.map((t) => {
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                title={t.description}
                style={{
                  fontSize: 10,
                  fontFamily: FONT.mono,
                  letterSpacing: '0.06em',
                  padding: '6px 14px',
                  borderRadius: 4,
                  border: `1px solid ${isActive ? t.color : 'transparent'}`,
                  background: isActive ? hexToBg(t.color, 0.1) : 'transparent',
                  color: isActive ? t.color : L.static,
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  textTransform: 'uppercase',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = L.mid;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = L.static;
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onExit}
            title="Exit"
            style={{
              background: 'none',
              border: `1px solid ${L.b2}`,
              borderRadius: 4,
              color: L.static,
              fontSize: 10,
              fontFamily: FONT.mono,
              letterSpacing: '0.06em',
              padding: '5px 12px',
              cursor: 'pointer',
              transition: 'color 0.12s, border-color 0.12s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = L.terra;
              e.currentTarget.style.borderColor = L.terra;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = L.static;
              e.currentTarget.style.borderColor = L.b2;
            }}
          >
            Exit
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        {activeTab === 'dashboard' && (
          <ProfessionalDashboard
            workspaceId={workspaceId}
            userId={userId}
            projectName={projectTitle}
            onNavigate={setActiveTab}
          />
        )}

        {activeTab === 'chat' && (
          <ProjectWorkstation
            userId={userId}
            userRecord={userRecord}
            supabase={supabase}
            workspaceSession={workspaceSession}
            voiceConfig={voiceConfig}
            onExit={onExit}
            // Parent owns sidebar state for the workstation's full
            // lifetime — no-op here so the embedded workstation can't
            // expand the sidebar when this tab unmounts.
            onSidebarCollapse={NOOP}
            embedded
          />
        )}

        {activeTab === 'resources' && (
          <ProfessionalResources
            workspaceId={workspaceId}
            projectId={projectId}
            userId={userId}
            supabase={supabase}
          />
        )}

        {activeTab === 'simulator' && (
          <ProfessionalSimulator
            workspaceId={workspaceId}
            projectId={projectId}
            userId={userId}
            supabase={supabase}
          />
        )}
      </div>
    </div>
  );
}

function hexToBg(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
